import { useState, useEffect } from 'react';

// Historical gold prices (approximate annual averages)
const goldPriceHistory: Record<number, number> = {
  2000: 279, 2001: 271, 2002: 310, 2003: 363, 2004: 409,
  2005: 445, 2006: 604, 2007: 695, 2008: 872, 2009: 972,
  2010: 1225, 2011: 1572, 2012: 1669, 2013: 1411, 2014: 1266,
  2015: 1160, 2016: 1251, 2017: 1257, 2018: 1268, 2019: 1393,
  2020: 1770, 2021: 1799, 2022: 1800, 2023: 1940, 2024: 2400,
  2025: 2650, 2026: 2650
};

// Cumulative inflation factors (1.0 = no change)
const inflationFactors: Record<number, number> = {
  2000: 0.55, 2001: 0.56, 2002: 0.57, 2003: 0.58, 2004: 0.59,
  2005: 0.61, 2006: 0.63, 2007: 0.65, 2008: 0.68, 2009: 0.68,
  2010: 0.70, 2011: 0.72, 2012: 0.74, 2013: 0.75, 2014: 0.77,
  2015: 0.77, 2016: 0.78, 2017: 0.80, 2018: 0.82, 2019: 0.84,
  2020: 0.85, 2021: 0.89, 2022: 0.96, 2023: 0.99, 2024: 1.02,
  2025: 1.05, 2026: 1.00
};

interface TimeSimulatorProps {
  onOptInClick: () => void;
}

export default function TimeSimulator({ onOptInClick }: TimeSimulatorProps) {
  const [year, setYear] = useState(2010);
  const [capital, setCapital] = useState(100000);
  const [cashValue, setCashValue] = useState(0);
  const [goldValue, setGoldValue] = useState(0);
  const [wealthLost, setWealthLost] = useState(0);

  useEffect(() => {
    // Calculate cash purchasing power today
    const inflationFactor = inflationFactors[year] || 0.7;
    const cashToday = capital * inflationFactor;
    setCashValue(cashToday);

    // Calculate gold value today
    const goldPriceThen = goldPriceHistory[year] || 500;
    const goldPriceNow = 2650; // Current gold price
    const ouncesCouldBuy = capital / goldPriceThen;
    const goldToday = ouncesCouldBuy * goldPriceNow;
    setGoldValue(goldToday);

    // Calculate wealth lost
    const lost = goldToday - cashToday;
    setWealthLost(lost);
  }, [year, capital]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const cashOpacity = Math.max(0.2, cashValue / capital);
  const goldHeight = Math.min(100, (goldValue / capital) * 50);

  return (
    <div className="w-full">
      {/* Headline */}
      <div className="text-center mb-8 md:mb-12">
        <h1 className="text-[40px] md:text-[56px] lg:text-[72px] font-black text-white mb-4 leading-[1.1]" style={{fontFamily: 'Oswald, sans-serif'}}>
          SEE WHAT INFLATION DID<br/><span className="text-[#D4AF37]">TO YOUR SAVINGS.</span>
        </h1>
        <p className="text-[16px] md:text-[20px] text-white/70 leading-[1.7] max-w-[800px] mx-auto">
          Compare the purchasing power of Cash vs. Gold over the last 20 years.
        </p>
      </div>

      {/* Interactive Controls */}
      <div className="bg-[#0A1628] border-2 border-[#D4AF37] rounded-lg p-6 md:p-10 mb-8">
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Year Slider */}
          <div>
            <label className="block text-[14px] md:text-[16px] font-bold text-[#D4AF37] mb-3">
              YEAR YOU STARTED SAVING
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="2000"
                max="2024"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="flex-1 h-3 bg-[#1A1A1A] rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #D4AF37 0%, #D4AF37 ${((year - 2000) / 24) * 100}%, #1A1A1A ${((year - 2000) / 24) * 100}%, #1A1A1A 100%)`
                }}
              />
              <span className="text-[24px] md:text-[32px] font-black text-[#D4AF37] min-w-[80px] text-right" style={{fontFamily: 'Oswald, sans-serif'}}>
                {year}
              </span>
            </div>
          </div>

          {/* Capital Slider */}
          <div>
            <label className="block text-[14px] md:text-[16px] font-bold text-[#D4AF37] mb-3">
              CAPITAL AMOUNT
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="10000"
                max="1000000"
                step="10000"
                value={capital}
                onChange={(e) => setCapital(parseInt(e.target.value))}
                className="flex-1 h-3 bg-[#1A1A1A] rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #D4AF37 0%, #D4AF37 ${((capital - 10000) / 990000) * 100}%, #1A1A1A ${((capital - 10000) / 990000) * 100}%, #1A1A1A 100%)`
                }}
              />
              <span className="text-[20px] md:text-[28px] font-black text-[#D4AF37] min-w-[120px] text-right" style={{fontFamily: 'Oswald, sans-serif'}}>
                {formatCurrency(capital)}
              </span>
            </div>
          </div>
        </div>

        {/* Big Number Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* LEFT: Cash Value */}
          <div className="bg-[#1A1A1A] border-2 border-[#FF4444] rounded-lg p-8 text-center">
            <p className="text-[12px] md:text-[14px] font-bold text-[#FF4444] mb-4 tracking-widest uppercase">
              Cash Value Today
            </p>
            <p className="text-[42px] md:text-[60px] font-black text-[#FF4444] leading-none" style={{fontFamily: 'Oswald, sans-serif'}}>
              {formatCurrency(cashValue)}
            </p>
            <p className="text-[12px] text-white/40 mt-3">Purchasing power after inflation</p>
          </div>

          {/* RIGHT: Gold Value */}
          <div className="bg-[#1A1A1A] border-2 border-[#D4AF37] rounded-lg p-8 text-center">
            <p className="text-[12px] md:text-[14px] font-bold text-[#D4AF37] mb-4 tracking-widest uppercase">
              Gold Value Today
            </p>
            <p className="text-[42px] md:text-[60px] font-black text-[#D4AF37] leading-none" style={{fontFamily: 'Oswald, sans-serif'}}>
              {formatCurrency(goldValue)}
            </p>
            <p className="text-[12px] text-white/40 mt-3">If you had bought gold in {year}</p>
          </div>
        </div>

        {/* Difference Metric */}
        <div className="bg-[#0D0D0D] border-2 border-white/20 rounded-lg p-6 md:p-8 text-center">
          <p className="text-[14px] md:text-[16px] font-bold text-white/60 mb-3 tracking-wider uppercase">
            Difference
          </p>
          <p className="text-[48px] md:text-[72px] lg:text-[88px] font-black text-white leading-none" style={{fontFamily: 'Oswald, sans-serif'}}>
            {formatCurrency(wealthLost)}
          </p>
          <p className="text-[12px] md:text-[14px] text-white/40 mt-3">
            The gap between holding cash vs. gold since {year}
          </p>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center">
        <h2 className="text-[22px] md:text-[32px] font-bold text-white mb-6 leading-[1.4]">
          You can't fix the past.{' '}
          <span className="text-[#D4AF37]">But you can make a smarter decision today.</span>
        </h2>
        <button
          onClick={onOptInClick}
          className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#000000] px-10 md:px-16 py-5 md:py-6 rounded-lg text-[16px] md:text-[20px] font-black transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-2xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
          style={{fontFamily: 'Oswald, sans-serif'}}
        >
          BEFORE YOU BUY ONE MORE OUNCE OF GOLD... TAKE THIS WITH YOU
          <i className="ri-arrow-down-line text-2xl"></i>
        </button>
      </div>
    </div>
  );
}